import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { makeAutoObservable, observable, ObservableMap } from 'mobx'
import { defaultMobxCacheMaxAge, type RootStore } from 'stores/RootStore'
import { FilesViewsClient, MinimalFilesViewSchema } from 'api/clients/FilesViewsClient'
import { makePersistable } from 'mobx-persist-store'

type AlmanacFileId = string
type Url = string
type WorkspaceId = number

export class FilesViewSchemaStore {
  rootStore: RootStore
  currentSchema?: MinimalFilesViewSchema = undefined
  schemas: ObservableMap<AlmanacFileId, MinimalFilesViewSchema[]> = observable.map()
  requests: ObservableMap<Url, Promise<MinimalFilesViewSchema[]>> = observable.map()

  constructor(rootStore: RootStore, shouldPersist = true) {
    this.rootStore = rootStore
    makeAutoObservable(this)

    if (shouldPersist) {
      makePersistable(this, {
        name: 'FilesViewSchemaStore',
        properties: ['schemas', 'currentSchema'],
        storage: window.localStorage,
        stringify: false,
        expireIn: defaultMobxCacheMaxAge,
      })
    }
  }

  add = (id: AlmanacFileId, schemas: MinimalFilesViewSchema[]) => {
    this.schemas.set(id, schemas)
  }

  get = (id: AlmanacFileId) => this.schemas.get(id)

  fetch = (id: AlmanacFileId, workspaceId: WorkspaceId) => {
    return new Promise<MinimalFilesViewSchema[]>((resolve, reject) => {
      const schemas = this.schemas.get(id)
      if (schemas) {
        resolve(schemas)
        return
      }

      let request = this.requests.get(id)
      if (!request) {
        request = FilesViewsClient.getSchemas(workspaceId, id).then((res) => {
          if (res.type !== SUCCESS_RESPONSE_TYPE) {
            throw new Error()
          }
          this.schemas.set(id, res.data.files_view_schemas)
          return res.data.files_view_schemas
        })
        this.requests.set(id, request)
      }
      request
        .then(resolve)
        .catch(reject)
        .finally(() => this.requests.delete(id))
    })
  }

  setCurrentSchema = (schema: MinimalFilesViewSchema | undefined) => {
    this.currentSchema = schema
  }

  getCurrentSchema = () => {
    return this.currentSchema
  }
}
